import { useCallback, useMemo, useRef, useState } from "react";

import {
  NotificationFilterForm,
  NotificationFilterFormRef,
  NotificationFilterFormValues,
} from "@components/Forms";
import { NotificationsTable } from "@components/Tables";

import { Box } from "@design-system/Box/Box";
import { Card } from "@design-system/Card/Card";
import { IconButton } from "@design-system/IconButton/IconButton";
import { SlideInPanel } from "@design-system/SlideInPanel";
import { Text } from "@design-system/Text/Text";

type Props = {
  initialUserTablePageIndex?: number;
};

export const Notifications = ({ initialUserTablePageIndex = 0 }: Props) => {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filterValues, setFilterValues] =
    useState<NotificationFilterFormValues>({
      clientId: undefined,
      notificationType: undefined,
      applicationNameSearch: undefined,
    });

  const filterFormRef = useRef<NotificationFilterFormRef>(null);

  const areFiltersActive = useMemo(() => {
    return Boolean(
      filterValues.clientId ||
        filterValues.notificationType ||
        filterValues.applicationNameSearch,
    );
  }, [filterValues]);

  const handleFilterClick = useCallback(() => {
    setIsFilterPanelOpen(true);
  }, []);

  const handleCloseFilterPanel = useCallback(() => {
    setIsFilterPanelOpen(false);
  }, []);

  const handleFiltersReset = useCallback(() => {
    setFilterValues({
      clientId: undefined,
      notificationType: undefined,
      applicationNameSearch: undefined,
    });
  }, []);

  const handleFilterChange = useCallback(
    (newValues: NotificationFilterFormValues) => {
      setFilterValues(newValues);
      // Close the panel when form is submitted (e.g., via Enter key)
      setIsFilterPanelOpen(false);
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    // Get current values from the form and apply them
    if (filterFormRef.current) {
      const currentFormValues = filterFormRef.current.getCurrentValues();
      setFilterValues(currentFormValues);
    }
  }, []);

  return (
    <>
      <Card grow>
        <Card.Header withBottomPadding>
          <Card.Header.Title>Notifications list</Card.Header.Title>
          <Card.Header.Actions>
            <Box direction="row" gap={8} align="center">
              {areFiltersActive && (
                <Text size={12} color={"textSecondary"}>
                  Filters are applied
                </Text>
              )}
              <IconButton
                size="sm"
                icon="faFilter"
                onClick={handleFilterClick}
                outline={"sm"}
                showBubble={areFiltersActive}
                tooltip="Show filters"
              />
            </Box>
          </Card.Header.Actions>
        </Card.Header>
        <Card.Body noPadding grow>
          <NotificationsTable
            initialPageIndex={initialUserTablePageIndex}
            {...(filterValues.clientId && {
              clientId: filterValues.clientId,
            })}
            {...(filterValues.notificationType && {
              notificationType: filterValues.notificationType,
            })}
            {...(filterValues.applicationNameSearch && {
              applicationNameSearch: filterValues.applicationNameSearch,
            })}
          />
        </Card.Body>
      </Card>

      <SlideInPanel
        isOpen={isFilterPanelOpen}
        onClose={handleCloseFilterPanel}
        onCancel={handleFiltersReset}
        onApply={handleApplyFilters}
        cancelButtonText={areFiltersActive ? "Clear filters" : undefined}
        applyButtonText="Apply filters"
        title="Notification Filters"
      >
        <NotificationFilterForm
          ref={filterFormRef}
          onFilterChange={handleFilterChange}
          values={filterValues}
          errorsAPI={undefined}
        />
      </SlideInPanel>
    </>
  );
};
